<template>
    <v-dialog v-model="show" content-class="small-dialog" width="560" persistent>
        <v-card>
            <v-card-title>
                <span>{{ dialogTitle }}</span>
            </v-card-title>
            <div class="card-body">
                <v-alert v-if="warningMessage" type="warning" text dense class="mb-0">
                    {{ translatedWarning }}
                </v-alert>

                <template v-if="!warningMessage">
                    <div v-if="failedEntities.length > 0" class="dispatch-section dispatch-section--failed mb-3">
                        <label class="d-block mb-1 text-white">
                            <v-icon small color="red darken-2" class="mx-1">mdi-alert-circle</v-icon>
                            <strong>{{ $t("res_Label_DispatchFailedFor") }}:</strong>
                        </label>
                        <div class="dispatch-hint mb-2">{{ $t("res_Msgs_DispatchNotCompletedRetry") }}</div>
                        <v-chip class="entity-chip mx-1 mb-1" small color="red darken-2" text-color="white"
                            v-for="entity in failedEntities" :key="'failed-' + entity.id">
                            {{ getLocalizedName(entity) }}
                        </v-chip>
                    </div>

                    <div v-if="dispatchedEntities.length > 0" class="dispatch-section dispatch-section--done"
                        :class="{ 'mb-3': viaEmailEntities.length > 0 }">
                        <label class="d-block mb-2 text-white">
                            <strong>{{ $t("res_Label_DispatchedSuccessfully") }}:</strong>
                        </label>
                        <v-chip class="entity-chip mx-1 mb-1" small color="green" text-color="white"
                            v-for="entity in dispatchedEntities" :key="'dispatched-' + entity.id">
                            {{ getLocalizedName(entity) }}
                        </v-chip>
                    </div>

                    <div v-if="viaEmailEntities.length > 0" class="dispatch-section dispatch-section--todo">
                        <label class="d-block mb-1 text-white">
                            <v-icon small color="amber darken-3" class="mx-1">mdi-email-alert-outline</v-icon>
                            <strong>{{ $t("res_Label_DispatchNotSubscribedToZajel") }}:</strong>
                        </label>
                        <div class="dispatch-hint mb-2">{{ $t("res_Msgs_DispatchFromViaEmailFolder") }}</div>
                        <v-chip class="entity-chip mx-1 mb-1" small color="black" text-color="white"
                            v-for="entity in viaEmailEntities" :key="'via-email-' + entity.id">
                            {{ getLocalizedName(entity) }}
                        </v-chip>
                    </div>

                    <label v-if="dispatchedEntities.length == 0 && viaEmailEntities.length == 0 && failedEntities.length == 0">
                        {{ $t("res_Msgs_CorrespondenceDispatched") }}
                    </label>
                </template>
            </div>
            <v-card-actions class="gap-2 sticky-actions">
                <v-spacer></v-spacer>
                <v-btn color="primary" width="100" @click="close">{{ $t("res_ButtonText_Close") }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    export default {
        name: 'DispatchResultDialog',
        props: {
            value: { type: Boolean, default: false },
            dispatchedEntities: { type: Array, default: () => [] },
            viaEmailEntities: { type: Array, default: () => [] },
            failedEntities: { type: Array, default: () => [] },
            // When set, the dialog shows this warning instead of the recipient breakdown.
            // Accepts either a translation key or a ready-made message coming from the API.
            warningMessage: { type: String, default: '' },
        },
        computed: {
            show: {
                get() { return this.value },
                set(val) { this.$emit('input', val) }
            },
            dialogTitle() {
                if (this.warningMessage) return this.$t("res_DialogTitle_DispatchCorrespondence")
                if (this.failedEntities.length > 0) return this.$t("res_Title_CorrespondenceDispatchFailed")

                return this.$t("res_Title_CorrespondenceDispatched")
            },
            translatedWarning() {
                if (!this.warningMessage) return ''

                return this.$te(this.warningMessage) ? this.$t(this.warningMessage) : this.warningMessage
            }
        },
        methods: {
            getLocalizedName(entity) {
                if (this.$i18n.locale == 'ar' && entity.nameAr) {
                    return entity.nameAr
                }

                return entity.name
            },
            close() {
                this.show = false
                this.$emit('closed')
            }
        }
    }
</script>

<style scoped>
    .dispatch-section {
        border-radius: 4px;
        padding: 12px;
    }

    /* The section that still needs the user to act is tinted so it is not distinguishable
       by the chip colour alone. */
    .dispatch-section--todo {
        background-color: rgba(255, 179, 0, 0.10);
        border: 1px solid rgba(255, 179, 0, 0.35);
    }

    /* Recipients the dispatch failed for. Tinted as well, so the failure does not rely on the chip
       colour alone, and placed first because it is the part that needs acting on. */
    .dispatch-section--failed {
        background-color: rgba(211, 47, 47, 0.10);
        border: 1px solid rgba(211, 47, 47, 0.35);
    }

    .dispatch-hint {
        font-size: 12px;
        opacity: 0.75;
        line-height: 1.4;
    }

    /* Entity names can be long, let the chip grow instead of clipping the name. */
    .entity-chip {
        height: auto !important;
        min-height: 24px;
        white-space: normal;
    }

    .entity-chip ::v-deep .v-chip__content {
        white-space: normal;
        padding: 3px 0;
    }
</style>
